import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/system';
import clsx from 'clsx';
import { useParams } from 'react-router-dom';
import { captableAction } from 'common/actions';
import {
  BREAKPOINT_ANALYSIS_SLUG,
  BREAKPOINT_ANALYSIS_TITLE,
  CAP_TABLE_SLUG,
  CAP_TABLE_TITLE,
  CASH_FLOW_LEDGER_SLUG,
  CASH_FLOW_LEDGER_TITLE,
  FUND_OWNERSHIP_SLUG,
  FUND_OWNERSHIP_TITLE,
} from 'common/constants/cap-table';
import { COMPANY_PAGE_ACTIONS } from 'common/constants/pageActions';
import {
  type CompanyPageActionHandler,
  type CompanyPageActionHandlers,
  useCompanyBreadcrumb,
  useCompanyPageAction,
} from 'common/hooks';
import { useStore } from 'common/store';
import { EXPORT_MODE_STYLES } from 'common/types/pdfExport';
import { CustomContainer } from 'components';
import { SecondaryNavigationAllocationStatusBadge } from 'components/ui';
import ExcelExportContext from 'context/ExcelExportContext';
import { useSecondaryNavigationContributionRegistration } from 'context/FirmBaseLayoutContext';
import LayoutContext from 'context/LayoutContext';
import { useExportLoadingBridge } from 'features/pdfExport';
import { CapTableDialog, NewCompanyDialog } from 'layouts/Main/components';
import useCapTableVersionFilter from 'pages/CapTable/cap-table/utilities/useCapTableVersionFilter';
import CapTableVersionSelector from 'pages/CapTable/CapTableVersionSelector';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import { useCompanyMeasurementDateFilter } from 'services/hooks';
import { useGetAllocationStatusBreadcrumb } from 'services/hooks/allocation';
import { getNumberValue, getObjectValue } from 'utilities';
import BreakpointAnalysis from './breakpoint-analysis';
import CapTable from './cap-table';
import CashFlows from './cash-distributions';
import FundOwnership from './fund-ownership';
import useCapTableGenerationRefresh from './useCapTableGenerationRefresh';

const CAP_TABLE_CONTAINER_CLASS = `${CAP_TABLE_SLUG}-container`;
const FUND_OWNERSHIP_CONTAINER_CLASS = `${FUND_OWNERSHIP_SLUG}-container`;
const BREAKPOINT_ANALYSIS_CONTAINER_CLASS = `${BREAKPOINT_ANALYSIS_SLUG}-container`;
const CASH_FLOW_LEDGER_CONTAINER_CLASS = `${CASH_FLOW_LEDGER_SLUG}-container`;
const CASH_FLOW_LEDGER_VERSION_SELECTOR_TOOLTIP = 'Cash Flow Ledger is only available for the Primary Cap Table.';

const CapTableView = () => {
  const { tableSlugParam } = useParams();
  const [{ captableInfo, companyInfo, companyMeasurementDateList }, dispatch] = useStore();
  const { id: currentCapTableId } = getObjectValue(captableInfo);
  const { setCurrentSelectedValues } = useContext(ExcelExportContext);

  // Refresh the cap table when a Process/Reprocess generation job completes,
  // wherever the user is on this page at that moment.
  useCapTableGenerationRefresh();

  const { isExportMode } = useContext(LayoutContext);
  const [showPageForm, setShowPageForm] = useState(false);
  const togglePageForm = useCallback(() => setShowPageForm(v => !v), []);
  const [showRelatedEntityDialog, setShowRelatedEntityDialog] = useState(false);
  const toggleRelatedEntityDialog = useCallback(() => setShowRelatedEntityDialog(v => !v), []);
  const [addSecurityAction, setAddSecurityAction] = useState<CompanyPageActionHandler>();
  const [saveAsNewProformaAction, setSaveAsNewProformaAction] = useState<CompanyPageActionHandler>();
  const handleAddSecurityActionChange = useCallback((action: CompanyPageActionHandler) => {
    setAddSecurityAction(() => action);
  }, []);
  const handleSaveAsNewProformaActionChange = useCallback((action: CompanyPageActionHandler | null) => {
    setSaveAsNewProformaAction(() => action ?? undefined);
  }, []);

  // Filters options
  const { MDDialogue, selectedMeasurementDate } = useCompanyMeasurementDateFilter();
  const {
    selectedVersion,
    onEditCaptable,
    updateCapTableVersions,
    saveAsNewProforma,
    isCreatingProforma,
    versionName,
    onAddRelatedEntity,
    capTableVersionsByCompany,
    capTableVersions,
    hasLoadedCapTableVersions,
    onVersionSelect,
    refreshCapTableVersions,
  } = useCapTableVersionFilter({
    selectedMeasurementDate,
    onAddRelatedEntity: toggleRelatedEntityDialog,
  });
  const unavailableAction = useMemo<CompanyPageActionHandler>(() => {
    if (companyMeasurementDateList === null || (selectedMeasurementDate && !hasLoadedCapTableVersions)) {
      return undefined;
    }

    return !selectedMeasurementDate || capTableVersions.length === 0 ? false : undefined;
  }, [capTableVersions.length, companyMeasurementDateList, hasLoadedCapTableVersions, selectedMeasurementDate]);
  const companyPageActionHandlers = useMemo<CompanyPageActionHandlers>(() => {
    if (tableSlugParam === FUND_OWNERSHIP_SLUG) {
      return {
        // FundOwnership consumes its own action once the spreadsheet is ready.
        [COMPANY_PAGE_ACTIONS.ADD_FUND_OWNERSHIP]: unavailableAction,
      };
    }

    if (tableSlugParam === CAP_TABLE_SLUG) {
      return {
        [COMPANY_PAGE_ACTIONS.ADD_SECURITY]: addSecurityAction ?? unavailableAction,
        [COMPANY_PAGE_ACTIONS.CREATE_PRO_FORMA_CAP_TABLE]: saveAsNewProformaAction ?? unavailableAction,
      };
    }

    return {};
  }, [addSecurityAction, saveAsNewProformaAction, tableSlugParam, unavailableAction]);

  useCompanyPageAction({ handlers: companyPageActionHandlers });

  const isBridgeSignalLoading = isExportMode && (!selectedMeasurementDate || !selectedVersion || !captableInfo?.id);
  useExportLoadingBridge({ target: PdfExportTargets.CAP_TABLE.id, enabled: isBridgeSignalLoading });
  useExportLoadingBridge({ target: PdfExportTargets.FUND_OWNERSHIP.id, enabled: isBridgeSignalLoading });
  useExportLoadingBridge({ target: PdfExportTargets.BREAKPOINT_ANALYSIS.id, enabled: isBridgeSignalLoading });
  useExportLoadingBridge({ target: PdfExportTargets.CASH_FLOW_LEDGER.id, enabled: isBridgeSignalLoading });

  const capTableAllocationId = useMemo(
    () => getNumberValue(companyInfo?.allocation_id as string | number | null | undefined),
    [companyInfo?.allocation_id]
  );
  const {
    allocationStatus,
    allocationStatusBreadcrumb,
    reloadAllocationBreadcrumbInfo,
  } = useGetAllocationStatusBreadcrumb(capTableAllocationId);

  useEffect(() => {
    setCurrentSelectedValues({
      cap_table: currentCapTableId,
      fo_cap_table: currentCapTableId,
      breakpoint_cap_table: currentCapTableId,
      waterfall_cap_table: currentCapTableId,
      cash_distributions_cap_table: currentCapTableId,
    });
  }, [currentCapTableId, setCurrentSelectedValues]);

  const activeTable = useMemo(() => {
    switch (tableSlugParam) {
      case CAP_TABLE_SLUG:
        return CAP_TABLE_TITLE;
      case FUND_OWNERSHIP_SLUG:
        return FUND_OWNERSHIP_TITLE;
      case BREAKPOINT_ANALYSIS_SLUG:
        return BREAKPOINT_ANALYSIS_TITLE;
      case CASH_FLOW_LEDGER_SLUG:
        return CASH_FLOW_LEDGER_TITLE;
      default:
        return '';
    }
  }, [tableSlugParam]);

  const { icon: status, title: label } = allocationStatusBreadcrumb;

  const captableBreadcrumb = useMemo(
    () => ({
      page: activeTable,
      item: versionName
        ? {
          label: versionName,
          allocationVersion: {
            label,
            status,
          },
        }
        : undefined,
    }),
    [activeTable, label, status, versionName]
  );

  useCompanyBreadcrumb(captableBreadcrumb);

  // Version selector for SecondaryNavigation
  const secondaryNavActionsNode = useMemo(
    () => {
      const isCashFlowLedgerTab = tableSlugParam === CASH_FLOW_LEDGER_SLUG;

      return Object.keys(capTableVersionsByCompany).length > 0 ? (
        <CapTableVersionSelector
          capTableVersionsByCompany={capTableVersionsByCompany}
          selectedVersion={selectedVersion}
          onVersionSelect={onVersionSelect}
          onEditVersion={togglePageForm}
          onSaveAsNewProforma={typeof saveAsNewProformaAction === 'function' ? saveAsNewProformaAction : undefined}
          disabled={isCashFlowLedgerTab}
          tooltipTitle={isCashFlowLedgerTab ? CASH_FLOW_LEDGER_VERSION_SELECTOR_TOOLTIP : undefined}
        />
      ) : null;
    },
    [
      capTableVersionsByCompany,
      selectedVersion,
      onVersionSelect,
      saveAsNewProformaAction,
      tableSlugParam,
      togglePageForm,
    ]
  );

  const secondaryNavStatusNode = useMemo(
    () => <SecondaryNavigationAllocationStatusBadge status={allocationStatus} />,
    [allocationStatus]
  );

  const secondaryNavContribution = useMemo(
    () => (
      secondaryNavActionsNode || secondaryNavStatusNode
        ? {
          actionsNode: secondaryNavActionsNode,
          status: secondaryNavStatusNode,
        }
        : null
    ),
    [secondaryNavActionsNode, secondaryNavStatusNode]
  );

  useSecondaryNavigationContributionRegistration(secondaryNavContribution);

  // Clean cap table info in the global store when leaving the page
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => dispatch(captableAction.setCaptableInfo({})), []);

  const renderSubTab = () => {
    switch (tableSlugParam) {
      case FUND_OWNERSHIP_SLUG:
        return (
          <CustomContainer
            className={clsx(FUND_OWNERSHIP_CONTAINER_CLASS, { 'is-export-mode': isExportMode })}
            data-ref={PdfExportTargets.FUND_OWNERSHIP.id}
            style={isExportMode ? EXPORT_MODE_STYLES : {}}>
            <Grid size={{ xs: 12 }} style={isExportMode ? EXPORT_MODE_STYLES : {}}>
              <FundOwnership
                updateCapTableVersions={updateCapTableVersions}
                saveAsNewProforma={saveAsNewProforma}
                isCreatingProforma={isCreatingProforma}
                selectedMeasurementDate={selectedMeasurementDate}
                reloadAllocationBreadcrumbInfo={reloadAllocationBreadcrumbInfo}
                capTableVersionsByCompany={capTableVersionsByCompany}
                onAddRelatedEntity={onAddRelatedEntity}
                onSaveAsNewProformaActionChange={handleSaveAsNewProformaActionChange}
              />
            </Grid>
          </CustomContainer>
        );
      case BREAKPOINT_ANALYSIS_SLUG:
        return (
          <CustomContainer className={BREAKPOINT_ANALYSIS_CONTAINER_CLASS}>
            <Grid size={{ xs: 12 }}>
              <BreakpointAnalysis
                updateCapTableVersions={updateCapTableVersions}
                saveAsNewProforma={saveAsNewProforma}
                isCreatingProforma={isCreatingProforma}
                onAddRelatedEntity={onAddRelatedEntity}
                onSaveAsNewProformaActionChange={handleSaveAsNewProformaActionChange}
                selectedMeasurementDate={selectedMeasurementDate}
              />
            </Grid>
          </CustomContainer>
        );
      case CASH_FLOW_LEDGER_SLUG:
        return (
          <CustomContainer
            className={CASH_FLOW_LEDGER_CONTAINER_CLASS}
            style={isExportMode ? EXPORT_MODE_STYLES : {}}>
            <Grid
              size={{ xs: 12 }}
              style={isExportMode ? EXPORT_MODE_STYLES : {}}
              data-ref={PdfExportTargets.CASH_FLOW_LEDGER.id}>
              <CashFlows
                selectedMeasurementDate={selectedMeasurementDate}
                capTableVersionsByCompany={capTableVersionsByCompany}
              />
            </Grid>
          </CustomContainer>
        );
      default:
        return (
          <CustomContainer className={clsx(CAP_TABLE_CONTAINER_CLASS, { 'is-export-mode': isExportMode })}>
            <Grid size={{ xs: 12 }}>
              <CapTable
                onEditCaptable={onEditCaptable}
                saveAsNewProforma={saveAsNewProforma}
                isCreatingProforma={isCreatingProforma}
                selectedMeasurementDate={selectedMeasurementDate}
                capTableId={currentCapTableId}
                onAddRelatedEntity={onAddRelatedEntity}
                onAddSecurityActionChange={handleAddSecurityActionChange}
                onSaveAsNewProformaActionChange={handleSaveAsNewProformaActionChange}
              />
            </Grid>
          </CustomContainer>
        );
    }
  };

  return (
    <>
      <Grid marginY={4}>
        {renderSubTab()}
      </Grid>
      <NewCompanyDialog
        open={showRelatedEntityDialog}
        onClose={toggleRelatedEntityDialog}
        onSave={refreshCapTableVersions}
        isRelatedEntity
      />

      {captableInfo && <CapTableDialog open={showPageForm} onClose={togglePageForm} onSave={onEditCaptable} />}
      <MDDialogue />
    </>
  );
};

export default CapTableView;

export { CapTableView, CapTable, FundOwnership, BreakpointAnalysis, CashFlows };
